/* eslint-disable react-hooks-ext/exhaustive-deps -- TODO(react-hooks): 3 hook(s) need dependency review; fix each and remove this directive */
import { getBackendUrl } from "@Utils";
import { useTranslation } from "@i18n";
import React, { useEffect, useState } from "react";
import { MdArrowDropDown } from "react-icons/md";
import { useSelector } from "react-redux";
import { useAppDispatch as useDispatch } from "@Reducers/hooks";
import axiosWrapper from "@AxiosWrapper";
import SelectDropDown from "@CubeComponents/Mui_Form/SelectDropDown";
import type {
  GetViewDataResponse,
  DeleteViewResponse,
  CreateViewResponse,
  GetSelectedViewerLayoutResponse,
  SetSelectedViewerLayoutResponse,
  UpdateViewResponse,
} from "@ServerTypes/layoutView/layoutView.schema";
import { IconButton, Menu, MenuItem, ListItemIcon, ListItemText, Popover, Box } from "@mui/material";
import TextFieldComponent from "@CubeComponents/Mui_Form/TextFieldComponent";
import { showToastNotification } from "@Reducers/toastSlice";
import { TOAST_TYPE_SUCCESS } from "@Services/constants";
import Accordion from "../../cubeComponent/html/accordion/materialUiAccordian/index";
import AccordionDetails from "@mui/material/AccordionDetails";
import AccordionSummary from "@mui/material/AccordionSummary";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import IconComponent from "@CubeComponents/icons";
import UserNameTagComponent from "@CubeComponents/html/UserNameTagComponent";
import ButtonComponent from "@CubeComponents/form/ButtonComponent";
import { BUTTON_TYPES } from "@CubeComponents/constants/globalVariable";
import { getDefaultViewDisplayName } from "@Helpers/constants";
import { LayoutData, DropdownItem, RenameData, DropdownContext, ViewsNewFlowProps } from "@Types/layout";
import Text from "@CubeComponents/html/Text/Text";
import { RootState } from "@Reducers/index";

function ViewsLayout({
  onClickLayout,
  onEditCallback,
  onDuplicateCallback,
  onDeleteCallback,
  headerName = "Layouts",
  enableUpdate = false,
  name = "Layout",
  createNewLayoutData,
  fetchParams = {},
  resourceType,
  selectedLayoutKey,
  triggerUpdateCurrentView,
  showSharedLayouts = true,
  onViewsLoaded = () => {},
}: Readonly<ViewsNewFlowProps>) {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const [renameData, setRenameData] = useState<RenameData>({
    _id: "",
    name: "",
  });
  const { userDetails } = useSelector((state: RootState) => state);
  const [currentLayoutData, setCurrentLayoutData] = useState<LayoutData>({} as LayoutData);
  const [layoutData, setLayoutData] = useState<LayoutData[]>([]);
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [dropdownAnchorEl, setDropdownAnchorEl] = useState<HTMLElement | null>(null);
  const [dropdownContext, setDropdownContext] = useState<DropdownContext | null>(null);
  const [dropDownItems, setdropDownItems] = useState<DropdownItem[]>([
    {
      text: t("Rename"),
      id: "rename",
      iconName: "editProfile",
    },
    {
      text: t("Duplicate"),
      id: "duplicate",
      iconName: "copy",
    },
    {
      text: t("Delete"),
      id: "delete",
      iconName: "delete",
    },
  ]);
  const openPopover = Boolean(anchorEl);
  const openDropdown = Boolean(dropdownAnchorEl);
  const id = openPopover ? "simple-popover" : undefined;

  const handleDropdownClick = (
    event: React.MouseEvent<HTMLElement>,
    layoutData: LayoutData,
    layoutDataIndex: number,
  ) => {
    setDropdownAnchorEl(event.currentTarget);
    setDropdownContext({ layoutData, layoutDataIndex });
  };

  const handleDropdownClose = () => {
    setDropdownAnchorEl(null);
    setDropdownContext(null);
  };

  const handleDropdownAction: any = (actionId: string) => {
    if (!dropdownContext) return;

    const { layoutData, layoutDataIndex } = dropdownContext;
    const args = { item: { id: actionId } };

    handleSelectedOption({ args, layoutData, layoutDataIndex });
    handleDropdownClose();
  };

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  useEffect(() => {
    getSelectedLayout();
    if (enableUpdate) {
      setdropDownItems([updateDropdownItem, ...dropDownItems]);
    }
  }, []);

  const handleClose = () => {
    setAnchorEl(null);
  };

  const getViews = async () => {
    const response = await axiosWrapper.post<GetViewDataResponse>(`${getBackendUrl()}/layoutView/getViewData`, {
      ...fetchParams,
      value: {
        where: [
          {
            field: "resourceType",
            operator: "equal",
            value: resourceType,
          },
        ],
      },
    });

    setLayoutData(response.data.data ?? []);
  };

  function handleSelectedOption({
    args,
    layoutData,
    layoutDataIndex,
  }: {
    args: { item: { id: string } };
    layoutData: LayoutData;
    layoutDataIndex: number;
  }) {
    if (args.item.id == "edit") {
      onEditCallback && onEditCallback({ layoutData, layoutDataIndex });
    }
    if (args.item.id == "rename") {
      setRenameData(layoutData);
    }
    if (args.item.id == "delete") {
      onClickOptionDelete({ layoutData, layoutDataIndex });
    }
    if (args.item.id == "duplicate") {
      onClickOptionDuplicate({ layoutData, layoutDataIndex });
    }
  }

  async function onClickOptionDelete({
    layoutData: deletedView,
    layoutDataIndex,
  }: {
    layoutData: LayoutData;
    layoutDataIndex: number;
  }) {
    const response = await axiosWrapper.post<DeleteViewResponse>(`${getBackendUrl()}/layoutView/deleteView`, {
      id: deletedView._id,
    });
    if (response.data.status) {
      setLayoutData((prevState: any) => {
        const newState = [...prevState];
        newState.splice(layoutDataIndex, 1);
        return newState;
      });
      dispatch(
        showToastNotification({
          content: t("viewDeleted", { viewName: deletedView.name }),
          type: TOAST_TYPE_SUCCESS,
        }),
      );

      if (currentLayoutData._id === deletedView._id) {
        const remainingViews = layoutData.filter((item: LayoutData) => item._id !== deletedView._id);
        const fallbackView: any = remainingViews.find((item: LayoutData) => item.isDefault) || remainingViews[0];
        handleClickLayout(fallbackView);
      }

      if (onDeleteCallback) {
        onDeleteCallback({ deleteData: deletedView, layoutDataIndex });
      }
    }
  }

  async function onClickOptionDuplicate({
    layoutData,
    layoutDataIndex,
  }: {
    layoutData: LayoutData;
    layoutDataIndex: number;
  }) {
    const response = await axiosWrapper.post<CreateViewResponse>(`${getBackendUrl()}/layoutView/createView`, {
      createData: {
        ...layoutData,
      },
    });
    if (response.data.status) {
      setLayoutData((prevState) => [...prevState, response.data.data!]);
      dispatch(
        showToastNotification({
          content: t("viewDuplicated", { viewName: response.data.data!.name }),
          type: TOAST_TYPE_SUCCESS,
        }),
      );
      onDuplicateCallback &&
        onDuplicateCallback({
          duplicatedData: layoutData,
          layoutDataIndex,
        });
    }
  }

  const updateDropdownItem = {
    text: "Update",
    id: "edit",
    iconName: "edit",
  };

  useEffect(() => {
    if (openPopover) {
      getViews();
    }
  }, [openPopover]);

  useEffect(() => {
    if (triggerUpdateCurrentView) {
      if (currentLayoutData.isDefault) {
        handleCreateViewOnDefaultChange();
      } else if (currentLayoutData._id) {
        const updatedData = {
          ...currentLayoutData.data,
          ...triggerUpdateCurrentView,
        };
        handleUpdateViewAPI({
          id: currentLayoutData._id,
          data: {
            data: updatedData,
          },
        });
        setCurrentLayoutData((prev: any) => ({
          ...prev,
          data: updatedData,
        }));
      }
    }
  }, [triggerUpdateCurrentView]);

  const getSelectedLayout = async () => {
    try {
      const selectedViewerLayout = await axiosWrapper.post<GetSelectedViewerLayoutResponse>(
        `${getBackendUrl()}/layoutView/getSelectedViewerLayout`,
        {
          key: selectedLayoutKey,
          ...fetchParams,
        },
      );
      const layoutData = selectedViewerLayout.data?.data;
      if (layoutData) {
        setCurrentLayoutData(layoutData);
        onViewsLoaded(layoutData);
      } else {
        // The grid holds its initial data fetch until views resolve — a missing saved
        // view must still be signalled or the grid would never load.
        onViewsLoaded(false);
      }
    } catch (_error) {
      onViewsLoaded(false);
      console.error("Failed to load selected layout:", _error); // CUBE-25437 - need to check the backend sentry, can't solve from frontend.
    }
  };

  const saveSelectedView = async (id: string) => {
    await axiosWrapper.post<SetSelectedViewerLayoutResponse>(`${getBackendUrl()}/layoutView/setSelectedViewerLayout`, {
      selectedViewerId: id,
      key: selectedLayoutKey,
      ...fetchParams,
    });
  };

  const handleClickLayout = (data: LayoutData) => {
    saveSelectedView(data._id);
    setCurrentLayoutData(data);
    onClickLayout?.(data);
  };

  const handleUpdateViewAPI = async ({ id, data }: { id: string; data: any }) => {
    await axiosWrapper.post<UpdateViewResponse>(`${getBackendUrl()}/layoutView/updateView`, {
      updateData: {
        ...data,
      },
      id,
    });
  };

  const handleUpdateView = async () => {
    const { _id: renameId, name: newName } = renameData;
    if (!renameId) return;

    const oldName = layoutData.find((item: any) => item._id === renameId)?.name;
    await handleUpdateViewAPI({
      id: renameId,
      data: {
        name: newName,
      },
    });
    setLayoutData((prevState: any) =>
      prevState.map((data: any) => {
        if (data._id == renameId) {
          return {
            ...data,
            name: newName,
          };
        }
        return data;
      }),
    );
    if (currentLayoutData._id == renameId) {
      setCurrentLayoutData((prev: any) => ({
        ...prev,
        name: newName,
      }));
    }
    dispatch(
      showToastNotification({
        content: t("viewRenamed", { oldName, newName }),
        type: TOAST_TYPE_SUCCESS,
      }),
    );
    setRenameData({
      _id: "",
      name: "",
    });
  };

  const layoutContent = (showShared: boolean) => {
    const layout: LayoutData[] = [];
    if (showShared) {
      layoutData.forEach((item: any, index: number) => {
        if (item.isShared) {
          layout.push({
            ...item,
            index,
          });
        }
      });
    } else {
      layoutData.forEach((item: any, index: number) => {
        if (!item.isShared) {
          layout.push({
            ...item,
            index,
          });
        }
      });
    }

    return (
      <Box className={`${layout.length > 0 ? "cursor-pointer" : "cursor-default"} font-normal`}>
        {layout.length > 0 ? (
          layout?.map((item: any) => (
            <React.Fragment key={item._id}>
              {renameData._id != item._id ? (
                <Box className="group transition-colors">
                  <Box
                    className="flex-row flex items-center h-10 hover:bg-[#F4F7FB]  rounded-md"
                    onClick={() => handleClickLayout(item)}
                  >
                    <Box className="text-[#192048] flex items-center font-source text-sm font-normal me-4 overflow-hidden whitespace-nowrap text-ellipsis flex-none">
                      {getDefaultViewDisplayName(item, t)}
                    </Box>
                    <Box className="flex-1 flex justify-end items-center me-2">
                      {item.isShared && <UserNameTagComponent id={item.createdByData?._id || item.createdBy} />}
                    </Box>

                    {currentLayoutData._id == item._id && (
                      <Box className="flex h-6 items-center justify-center gap-1 ms-2 rounded-lg bg-[#6BBBE3] px-2 py-1 text-xs font-semibold leading-[120%] text-white flex-none">
                        <Text className="leading-none">{t("Current")}</Text>
                      </Box>
                    )}
                    {item.isDefault && (
                      <Box className="flex h-6 ms-2 items-center justify-center gap-1 rounded-lg bg-[#666A84] px-2 py-1 text-xs font-semibold leading-[120%] text-white me-2 flex-none">
                        <Text className="leading-none">{t("Default")}</Text>
                      </Box>
                    )}
                    <Box className="flex-none w-8 flex items-center justify-center">
                      <Box className="group-hover:block" data-testid="threeDotsInViewsItems">
                        {!item.isDefault && userDetails._id == item.createdBy && (
                          <IconButton
                            size="small"
                            onClick={(e: React.MouseEvent<HTMLElement, MouseEvent>) => {
                              e.preventDefault();
                              e.stopPropagation();
                              handleDropdownClick(e, item, item.index);
                            }}
                            className="preventLayoutClose opacity-0 group-hover:opacity-100 transition-opacity"
                            sx={{
                              padding: 0,
                              height: "1rem",
                              minWidth: "auto",
                            }}
                          >
                            <IconComponent
                              name="HorizontalThreeDots"
                              className="cursor-pointer"
                              height="1rem"
                              width="1rem"
                            />
                          </IconButton>
                        )}
                      </Box>
                    </Box>
                  </Box>
                </Box>
              ) : (
                <Box className="flex items-center gap-2 p-2 bg-[#F4F7FB] rounded-md my-1">
                  <TextFieldComponent
                    type="text"
                    value={renameData.name}
                    onChange={(args: any) => {
                      setRenameData((prev) => ({ ...prev, name: args.target.value }));
                    }}
                  />
                  <IconComponent
                    name="cross"
                    width="1rem"
                    height="1rem"
                    className="cursor-pointer text-red-500"
                    onClick={() =>
                      setRenameData({
                        _id: "",
                        name: "",
                      })
                    }
                    data-testid="cancelRename"
                  />
                  <IconComponent
                    name="tick2"
                    width="1rem"
                    height="1rem"
                    className="cursor-pointer text-green-500"
                    onClick={handleUpdateView}
                    data-testid="confirmRename"
                  />
                </Box>
              )}
            </React.Fragment>
          ))
        ) : (
          <Box className="flex flex-col items-center justify-center py-4">
            <IconComponent name="nodatasvg" width="8rem" height="8rem" />
            <Text className="text-12 text-[#666A84] mt-2 leading-none">{t("No Data")}</Text>
          </Box>
        )}
      </Box>
    );
  };

  const handleCreateViewOnDefaultChange = async () => {
    const baseName = currentLayoutData.name || name;

    let nextName = "";
    let counter = 1;
    let nameExists = true;

    while (nameExists) {
      nextName = `${baseName} (${counter})`;
      nameExists = layoutData.some((layout: any) => layout.name.toLowerCase() === nextName.toLowerCase());
      counter++;
    }

    const data: any = {
      resourceType,
      data: createNewLayoutData(),
      name: nextName,
      ...fetchParams,
    };

    const created = await axiosWrapper.post<CreateViewResponse>(`${getBackendUrl()}/layoutView/createView`, {
      createData: { ...data, isShared: false },
    });

    if (created.data.status) {
      setLayoutData((prevState: any) => [...prevState, { ...created.data.data, createdByData: userDetails }]);

      handleClickLayout(created.data.data);

      dispatch(
        showToastNotification({
          content: t("viewCreated", { name: created.data.data.name }),
          type: TOAST_TYPE_SUCCESS,
        }),
      );
    }
  };
  async function onSelectCreate(args: any) {
    const isShared = args.item.id === "sharedView";

    const sharedName = isShared ? "Shared " : "";
    const data: any = {
      resourceType,
      data: createNewLayoutData(),
      name: `${sharedName}${name} ${
        isShared
          ? layoutData.filter((item: any) => item.isShared).length + 1
          : layoutData.filter((item: any) => !item.isShared).length + 1
      }`,
      ...fetchParams,
    };

    const created = await axiosWrapper.post<CreateViewResponse>(`${getBackendUrl()}/layoutView/createView`, {
      createData: { ...data, isShared },
    });
    if (created.data.status) {
      setLayoutData((prevState: any) => [...prevState, { ...created.data.data, createdByData: userDetails }]);
      handleClickLayout(created.data.data);
      dispatch(
        showToastNotification({
          content: t("viewCreated", {
            name: created.data.data.name,
          }),
          type: TOAST_TYPE_SUCCESS,
        }),
      );
    }
  }

  return (
    <Box id="custom_viewLayout" className="relative">
      <ButtonComponent
        buttonType={BUTTON_TYPES.TEXT_BUTTON}
        aria-describedby={id}
        onClick={handleClick}
        data-testid="viewsBtn"
      >
        <Box className="flex items-center gap-1">
          <span className="font-semibold">{getDefaultViewDisplayName(currentLayoutData, t)}</span>
          <MdArrowDropDown size="1.2rem" />
        </Box>
      </ButtonComponent>

      <Popover
        id={id}
        open={openPopover}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "left",
        }}
        container={document.getElementById("custom_viewLayout")}
        slotProps={{
          paper: {
            style: {
              width: "29.375rem",
              boxShadow: "0px 0.625rem 0.9375rem -0.1875rem rgba(0,0,0,0.1)",
              borderRadius: "0.75rem",
            },
          },
        }}
      >
        <Box
          data-testid="layoutDropdown"
          onClick={(e: { preventDefault: () => any }) => e.preventDefault()}
          className="flex flex-col justify-between overflow-auto p-6 bg-white"
        >
          <Box
            className="flex justify-between pb-3 mb-4 items-center border-b border-[#C6D8EC]"
            data-testid="addNewLayout"
          >
            <Box className="text-[#192048] font-source text-2xl font-semibold leading-[1.2]" data-testid="ViewsHeading">
              {headerName}
            </Box>
            <SelectDropDown
              data-testid="createBtn"
              items={[
                {
                  text: t("My View"),
                  id: "myView",
                },
                {
                  text: t("Shared View"),
                  id: "sharedView",
                },
              ]}
              select={onSelectCreate}
              placeholder={t("Create")}
              buttonType="backgroundButton"
            />
          </Box>
          <Box className="max-h-[25rem] overflow-y-auto">
            <Accordion
              defaultExpanded
              data-testid="myViewsSection"
              sx={{
                boxShadow: "none",
                "&:before": { display: "none" },
                margin: "0 !important",
                marginTop: "0.625rem !important",
              }}
            >
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                data-testid="myViewsHeader"
                sx={{
                  padding: 0,
                  minHeight: "2.5rem !important",
                  "& .MuiAccordionSummary-content": { margin: "0.5rem 0 !important" },
                }}
              >
                <Box
                  className="text-[#192048] text-base font-bold leading-[125%]  tracking-tight"
                  data-testid="myViews"
                >
                  {`My ${headerName}`}
                </Box>
              </AccordionSummary>
              <AccordionDetails sx={{ padding: 0 }}>{layoutContent(false)}</AccordionDetails>
            </Accordion>
            {showSharedLayouts && (
              <Accordion
                defaultExpanded
                data-testid="sharedViewsSection"
                sx={{
                  boxShadow: "none",
                  "&:before": { display: "none" },
                  margin: "0 !important",
                  marginTop: "0.625rem !important",
                }}
              >
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon />}
                  data-testid="sharedLayoutsHeader"
                  sx={{
                    padding: 0,
                    minHeight: "2.5rem !important",
                    "& .MuiAccordionSummary-content": { margin: "0.5rem 0 !important" },
                  }}
                >
                  <Box
                    className="text-[#192048] text-base font-bold leading-[125%]  tracking-tight"
                    data-testid="sharedViews"
                  >
                    {`Shared ${headerName}`}
                  </Box>
                </AccordionSummary>
                <AccordionDetails sx={{ padding: 0 }}>{layoutContent(true)}</AccordionDetails>
              </Accordion>
            )}
          </Box>
          <Menu
            anchorEl={dropdownAnchorEl}
            open={openDropdown}
            onClose={handleDropdownClose}
            onClick={(e: React.SyntheticEvent) => e.stopPropagation()}
            disablePortal
            anchorOrigin={{
              vertical: "bottom",
              horizontal: "right",
            }}
            transformOrigin={{
              vertical: "top",
              horizontal: "right",
            }}
            slotProps={{
              paper: {
                sx: {
                  boxShadow: "0px 0.25rem 1rem 0px rgba(0, 0, 0, 0.12)",
                  borderRadius: "0.5rem",
                },
              },
            }}
          >
            {dropDownItems.map((item: any) => (
              <MenuItem
                key={item.id}
                onClick={() => handleDropdownAction(item.id)}
                sx={{ fontSize: "0.875rem", minHeight: "2rem" }}
              >
                <ListItemIcon sx={{ minWidth: "1.875rem" }}>
                  <IconComponent name={item.iconName} width="1.5rem" height="1.5rem" />
                </ListItemIcon>
                <ListItemText primary={item.text} />
              </MenuItem>
            ))}
          </Menu>
        </Box>
      </Popover>
    </Box>
  );
}

export default ViewsLayout;
